import { Button, Icon } from "@sellar-io/components";
import { twMerge } from "tailwind-merge";
import { useState, type ReactNode } from "react";
import { SwitchBusinessNavigationContent } from "./SwitchBusinessNavigationContent";
import { useNavigationOverlay } from "./NavigationOverlayContext.shared";

import { hasBusinessMembership } from "../../../hooks/api/v2/apiV2Request";
import { useBusinessUserInvite } from "../../../hooks/api/v2/businessUser/useBusinessUserInvite";
import { useFirebaseUser } from "../../../hooks/useFirebaseUser.shared";
import { useSellarCurrentBusiness } from "../../../hooks/useSellarCurrentBusiness.shared";
import { BusinessInvitationForm } from "./BusinessInvitationForm";
import { NavigationOverlay } from "./NavigationOverlay";
import { BusinessNavigationOverlayContent } from "./BusinessNavigationOverlayContent";

const BusinessInvitation = ({
  businessId,
  businessName,
  children,
}: {
  businessId: number;
  businessName: string;
  children: (form: ReactNode) => ReactNode;
}) => {
  const invitation = useBusinessUserInvite(businessId);
  return (
    <BusinessInvitationForm
      businessName={businessName}
      onInvite={(email) => invitation.mutateAsync({ email })}
    >
      {children}
    </BusinessInvitationForm>
  );
};

export const BusinessNavigationOverlay = ({
  className,
}: {
  className?: string;
}) => {
  const { business } = useSellarCurrentBusiness();
  const { activeOverlay, closeOverlay } = useNavigationOverlay();
  const [switching, setSwitching] = useState(false);
  if (switching && activeOverlay !== "business") {
    setSwitching(false);
  }
  const { user } = useFirebaseUser();
  if (!business) {
    return null;
  }
  const canInvite = hasBusinessMembership({ user, business });
  const renderOverlay = (invitation?: ReactNode) => (
    <NavigationOverlay
      id="business"
      title={switching ? "Switch business" : "Business"}
      contentClassName={switching ? undefined : "gap-0 p-0"}
      trigger={(props) => (
        <Button
          {...props}
          color="transparent"
          aria-label={`Business account: ${business.name}`}
          className={twMerge(
            "hidden h-[38px] max-w-48 gap-2 rounded-full border border-solid border-zinc-200 bg-zinc-100 px-3 py-0 text-sm font-medium tracking-normal normal-case hover:bg-zinc-200 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-purple-600 aria-expanded:border-zinc-300 aria-expanded:bg-zinc-200 lg:flex",
            className,
          )}
        >
          <Icon icon="STOREFRONT_OUTLINE" size="16px" />
          <span className="truncate">{business.name}</span>
          <Icon icon="CHEVRON_DOWN" size="16px" />
        </Button>
      )}
    >
      {switching ? (
        <SwitchBusinessNavigationContent
          onBack={() => setSwitching(false)}
          onClose={() => closeOverlay("business")}
        />
      ) : (
        <BusinessNavigationOverlayContent
          businessName={business.name}
          companyName={
            business.BusinessLegalEntity?.name ||
            business.registeredCompanyName ||
            undefined
          }
          invitation={invitation}
          onSwitchBusiness={() => setSwitching(true)}
        />
      )}
    </NavigationOverlay>
  );

  // Keep request/form state alive when only the popover content unmounts.
  return canInvite ? (
    <BusinessInvitation
      key={business.id}
      businessId={business.id}
      businessName={business.name}
    >
      {renderOverlay}
    </BusinessInvitation>
  ) : (
    renderOverlay()
  );
};
